import type { Tone } from '../../components/StatusBadge';
import type { AuthedRequest } from '../auth/AuthProvider';

/** Mirrors GET /api/v1/system/media. */
export interface ThreadLoad {
  name: string;
  /** Busy percentage, 0–100. */
  load: number;
  delay_ms: number;
}

export interface MediaNode {
  version: string;
  build: string;
  /** Open TCP/UDP sessions on the engine (players, pulls, WebRTC). */
  connections: number;
  /** The figures below cover every tenant on the node, as aggregates. */
  streams: number;
  viewers: number;
  bytes_per_second: number;
  event_loops: ThreadLoad[];
  work_loops: ThreadLoad[];
}

/** Whose camera a row is: set in the super administrator's view of every tenant (0.67.0). */
export interface TenantRef {
  id: string;
  name: string;
}

/** The tenant's name for a row, or a dash (absent before 0.67.0). */
export const tenantName = (t: TenantRef | null | undefined) => t?.name ?? '—';

export interface MediaStream {
  tenant?: TenantRef | null;
  device: { id: string; name: string; site: string };
  /** Null when the ingest belongs to a source that no longer exists. */
  profile_token: string | null;
  profile_name: string;
  origin: string;
  alive_seconds: number;
  viewers: number;
  bytes_per_second: number;
  total_bytes: number;
  protocols: string[];
  video: { codec: string; width: number; height: number; frame_rate: number } | null;
  audio: string | null;
  recording: boolean;
  /** The media node the stream is on (ADR-048). */
  node: { id: string; name: string };
}

/** One media node (ADR-048); `node` sums the available ones. */
export interface MediaServerNode {
  id: string;
  name: string;
  status: 'up' | 'down';
  version: string;
  connections: number;
  streams: number;
  viewers: number;
  bytes_per_second: number;
}

export interface MediaStatus {
  status: 'up' | 'down';
  checked_at: string;
  node: MediaNode | null;
  streams: MediaStream[];
  /** Every stream of the scope; `streams` holds at most 500 (absent before 0.67.0). */
  streams_total?: number;
  nodes: MediaServerNode[];
}

export const mediaStatusKey = ['system', 'media'] as const;

export const getMediaStatus = (req: AuthedRequest) =>
  req<MediaStatus>('GET', '/api/v1/system/media');

/** Someone (or something) watching a camera now (ADR-025). */
export interface MediaViewer {
  kind: 'live' | 'device_playback' | 'integration' | 'unknown';
  transport: string;
  user: string | null;
  user_email: string | null;
  integration: string | null;
  ip: string | null;
  user_agent: string | null;
  /** The camera's secondary stream. */
  sub: boolean;
  since: string | null;
  /** The media node serving this viewer when it is not the camera's (ADR-049). */
  via: string | null;
}

export interface CameraViewers {
  tenant?: TenantRef | null;
  device: { id: string; name: string; site: string };
  /** The camera's media node. */
  node: string;
  viewers: MediaViewer[];
}

export const mediaViewersKey = ['system', 'media', 'viewers'] as const;

/** Watched cameras: at most 500 in `items`; `total` counts them all (0.67.0). */
export interface ViewersReport {
  items: CameraViewers[];
  total: number;
}

export async function getMediaViewers(req: AuthedRequest): Promise<ViewersReport> {
  const r = await req<{ items: CameraViewers[]; total?: number }>(
    'GET',
    '/api/v1/system/media/viewers',
  );
  return { items: r.items, total: r.total ?? r.items.length };
}

/** "mostrando N de M" when a list was capped by the API, else null. */
export const shownOf = (shown: number, total: number | undefined, noun: string) =>
  total !== undefined && total > shown
    ? `Mostrando ${shown.toLocaleString('pt-BR')} de ${total.toLocaleString('pt-BR')} ${noun}.`
    : null;

export const viewerKindLabels: Record<MediaViewer['kind'], string> = {
  live: 'Ao vivo',
  device_playback: 'Reprodução no equipamento',
  integration: 'Integração',
  unknown: 'Não identificado',
};

export const transportLabels: Record<string, string> = {
  webrtc: 'WebRTC',
  hls: 'HLS',
  rtsp: 'RTSP',
  rtmp: 'RTMP',
};

/** Who a viewer is, in one line. */
export const viewerName = (v: MediaViewer) =>
  v.integration ?? v.user ?? (v.kind === 'unknown' ? 'Não identificado' : 'Usuário removido');

/** Browser and system from a User-Agent, e.g. "Chrome · Windows". */
export function describeAgent(ua: string | null): string {
  if (!ua) return '—';
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /OPR\/|Opera/.test(ua)
      ? 'Opera'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Chrome\//.test(ua)
          ? 'Chrome'
          : /Safari\//.test(ua)
            ? 'Safari'
            : 'Navegador';
  const os = /Android/.test(ua)
    ? 'Android'
    : /iPhone|iPad|iOS/.test(ua)
      ? 'iOS'
      : /Windows/.test(ua)
        ? 'Windows'
        : /Mac OS X|Macintosh/.test(ua)
          ? 'macOS'
          : /Linux/.test(ua)
            ? 'Linux'
            : '';
  return os ? `${browser} · ${os}` : browser;
}

/** Bytes per second as a network bit rate (kbit/s, Mbit/s). */
export function formatBitrate(bytesPerSecond: number): string {
  const bits = bytesPerSecond * 8;
  if (bits >= 1e9) return `${(bits / 1e9).toFixed(2)} Gbit/s`;
  if (bits >= 1e6) return `${(bits / 1e6).toFixed(bits >= 1e8 ? 0 : 1)} Mbit/s`;
  if (bits >= 1e3) return `${Math.round(bits / 1e3)} kbit/s`;
  return `${Math.round(bits)} bit/s`;
}

export function formatBytes(n: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i] ?? 'B'}`;
}

/** Compact duration: "45 s", "12 min", "3 h 05 min", "2 d 4 h". */
export function formatUptime(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${String(s)} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${String(m)} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${String(h)} h ${String(m % 60).padStart(2, '0')} min`;
  return `${String(Math.floor(h / 24))} d ${String(h % 24)} h`;
}

/** Thread load thresholds: the engine drops frames when its loops saturate. */
export function loadTone(load: number): Tone {
  if (load >= 90) return 'crit';
  if (load >= 70) return 'warn';
  return 'ok';
}

export const loadLabel: Record<Tone, string> = {
  ok: 'Normal',
  warn: 'Elevada',
  crit: 'Saturada',
  neutral: '—',
};

export function summarizeLoops(loops: ThreadLoad[]) {
  if (loops.length === 0) return { avg: 0, max: 0, maxDelay: 0 };
  const sum = loops.reduce((a, l) => a + l.load, 0);
  return {
    avg: Math.round(sum / loops.length),
    max: Math.max(...loops.map((l) => l.load)),
    maxDelay: Math.max(...loops.map((l) => l.delay_ms)),
  };
}
